import { Action } from '@wix/component-protocol/schema';
import {
  NativeActionContext,
  NativeActionNames,
  SystemActionContext,
} from '@wix/editor-platform-host-integration-apis';
import { createCustomisationAPI } from './customActions/customisationAPI';
import { ActionContextAPI } from './ActionContextAPI';
import { createActionPanelAPI } from './actionPanelAPI';
import { isActionContextContributor } from './actionPanelAPI/IActionContextContributor';
import { ExtendedPlatformContext, PlatformContext } from './types';

export class ActionContext {
  constructor(private readonly ctx: PlatformContext) {}

  private createAPIs(actionName: string, actionDefinition: Action) {
    const actionPanelAPI = createActionPanelAPI(
      actionDefinition,
      actionName,
      this.ctx,
    );

    const customisationAPI = createCustomisationAPI(
      actionDefinition,
      actionName,
      this.ctx,
    );

    return {
      actionPanelAPI,
      customisationAPI,
    };
  }

  getPlatformContext(): PlatformContext {
    return this.ctx;
  }

  createExtendedPlatformContext<TActionName extends string>(
    actionName: TActionName,
    actionDefinition: Action,
  ): ExtendedPlatformContext {
    const actionPanelAPI = createActionPanelAPI(
      actionDefinition,
      actionName,
      this.ctx,
    );
    const customisationAPI = createCustomisationAPI(
      actionDefinition,
      actionName,
      this.ctx,
    );

    return {
      ...this.ctx,
      actionPanelAPI,
      customisationAPI,
    };
  }

  createNativeActionContext(
    actionName: NativeActionNames,
    actionDefinition: Action,
  ): NativeActionContext {
    const { actionPanelAPI, customisationAPI } = this.createAPIs(
      actionName,
      actionDefinition,
    );

    const overrides = isActionContextContributor(actionPanelAPI)
      ? actionPanelAPI.getActionContextOverrides()
      : {};

    const contextAPI = new ActionContextAPI(
      actionDefinition,
      actionName,
      this.ctx,
      overrides,
    );

    return {
      editorPointer: this.ctx.editorPointer,
      editorPointers: this.ctx.editorPointers,
      actionPanelAPI,
      contextAPI,
      customisationAPI,
    };
  }

  createSystemActionContext(
    actionName: string,
    actionDefinition: Action,
  ): SystemActionContext {
    const contextAPI = new ActionContextAPI(
      actionDefinition,
      actionName,
      this.ctx,
    );

    return {
      editorPointer: this.ctx.editorPointer,
      editorPointers: this.ctx.editorPointers,
      contextAPI,
    };
  }
}
